import type { MeshResult } from "./scriptRunner";
import type { ParamMeta } from "../workers/cadWorker";

export interface CachedResult {
  mesh: MeshResult;
  params?: ParamMeta[];
}

const DB_NAME = "cad-plm-cache";
const STORE_NAME = "geometry";
const DB_VERSION = 4;

export async function hashScript(script: string): Promise<string> {
  const encoded = new TextEncoder().encode(script.trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }
        db.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbPromise = null;
        reject(req.error);
      };
    });
  }
  return dbPromise;
}

export async function getFromCache(hash: string): Promise<CachedResult | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(hash);
      req.onsuccess = () => {
        const val = req.result;
        if (!val || !val.vertices) {
          resolve(null);
          return;
        }
        resolve({
          mesh: {
            vertices: new Float32Array(val.vertices),
            indices: new Uint32Array(val.indices),
            normals: new Float32Array(val.normals),
          },
          params: val.params,
        });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function putInCache(
  hash: string,
  mesh: MeshResult,
  params?: ParamMeta[]
): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put(
        {
          vertices: Array.from(mesh.vertices),
          indices: Array.from(mesh.indices),
          normals: Array.from(mesh.normals),
          params,
        },
        hash
      );
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Non-fatal
  }
}
